import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, COMPANY } from "../lib/constants";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl"
      style={{
        background: scrolled ? "rgba(253,251,247,0.92)" : "rgba(253,251,247,0.78)",
        borderBottom: scrolled ? "1px solid var(--m-border)" : "1px solid transparent",
        transition: "all 250ms ease",
      }}
      data-testid="main-navbar"
    >
      <div className="container-x flex items-center justify-between py-4 md:py-5">
        <Link to="/" data-testid="nav-logo-link">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[var(--m-teal)]" : "text-[var(--m-ink)] hover:text-[var(--m-teal)]"
                }`
              }
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            className="flex items-center gap-2 text-sm text-[var(--m-ink-2)] hover:text-[var(--m-teal)]"
            data-testid="nav-phone"
          >
            <Phone size={15} strokeWidth={1.6} />
            {COMPANY.phone}
          </a>
          <Link to="/quotation" className="btn-primary" data-testid="nav-cta-quote">
            Get Quote
          </Link>
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--m-border)] bg-[var(--m-bg)]" data-testid="nav-mobile-menu">
          <div className="container-x py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-base py-2 ${isActive ? "text-[var(--m-teal)] font-semibold" : "text-[var(--m-ink)]"}`
                }
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="divider-h my-2" />
            <a href={`tel:${COMPANY.phoneRaw}`} className="text-sm text-[var(--m-ink-2)] flex items-center gap-2">
              <Phone size={15} /> {COMPANY.phone}
            </a>
            <Link to="/quotation" className="btn-primary w-full justify-center" data-testid="nav-mobile-cta">
              Get Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
